<template>
  <div class="package-admin-container">
    <v-row class="fill-height" no-gutters>
      <v-col cols="12" md="4" lg="3" class="sidebar-col">
        <v-card class="sidebar-card" elevation="2">
          <v-card-text class="pa-4">
            <v-select
              :items="laboratories"
              v-model="laboratory"
              label="Laboratorio"
              item-text="name"
              return-object
              hide-details
              outlined
              dense
              class="mb-4"
            ></v-select>
            <div class="lab-info mb-4">
              <div class="d-flex justify-space-between">
                <span class="text-caption grey--text">Municipio:</span>
                <span class="font-weight-medium">{{
                  laboratory.city || "-"
                }}</span>
              </div>
              <div class="d-flex justify-space-between">
                <span class="text-caption grey--text">Estado:</span>
                <span class="font-weight-medium">{{
                  laboratory.state || "-"
                }}</span>
              </div>
            </div>
            <v-combobox
              v-model="cropsLaboratory"
              :items="crops"
              label="Cultivo/s"
              item-text="es_text"
              multiple
              outlined
              dense
              hide-details
              class="mb-4"
            >
              <template v-slot:prepend-item>
                <v-list-item @click="toggle">
                  <v-list-item-title class="text-caption">
                    {{
                      selectedAllCrops
                        ? "Deseleccionar todos"
                        : "Seleccionar todos"
                    }}
                  </v-list-item-title>
                </v-list-item>
                <v-divider class="mt-2"></v-divider>
              </template>
              <template v-slot:selection="{ index, item }">
                <v-chip v-if="index < 2" color="primary" small class="mr-1">
                  {{ item.es_text }}
                </v-chip>
                <span v-if="index === 2" class="grey--text text-caption">
                  +{{ cropsLaboratory.length - 2 }} más
                </span>
              </template>
            </v-combobox>
            <v-combobox
              v-model="headquarter"
              :items="headquarters"
              label="Canal"
              item-text="companyName"
              outlined
              dense
              hide-details
              class="mb-4"
            ></v-combobox>
            <v-btn
              color="warning"
              block
              class="mt-4"
              :disabled="hasPermission('lab_unedit') || !hasChanges"
              @click="updateLabData"
              depressed
            >
              <v-icon left small>mdi-content-save</v-icon>
              <span class="text-caption">Guardar cambios</span>
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="8" lg="9" class="content-col">
        <v-card class="content-card" elevation="2">
          <v-card-title class="px-4 py-3">
            <span class="text-subtitle-1 font-weight-medium"
              >Administración de paquetes</span
            >
            <v-spacer></v-spacer>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  fab
                  color="primary"
                  small
                  @click="refreshTable"
                  class="mr-2"
                  title="Actualizar"
                >
                  <v-icon small>mdi-refresh</v-icon>
                </v-btn>
              </template>
              <span>Refrescar tabla</span>
            </v-tooltip>
          </v-card-title>

          <v-card-text class="pa-0">
            <packages-table
              :laboratory="laboratory"
              v-if="laboratory"
              @package_selected="handlePackageSelected"
              class="packages-table"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <reactions-modal
      v-model="showReactionsModal"
      :laboratory="laboratory"
      @packages_updated="getPackagesLab"
      v-if="showReactionsModal"
    />
  </div>
</template>

<script>
import { mapActions, mapState, mapMutations } from "vuex";
import cropsJson from "@/data/crops.json";
import PackagesTable from "./PackagesTable.vue";
import ReactionsModal from "./modals/ReactionModal.vue";

export default {
  name: "PackageAdmin",
  components: {
    PackagesTable,
    ReactionsModal,
  },
  data() {
    return {
      laboratory: { name: "-", city: "-" },
      reactions: 1,
      crops: cropsJson,
      cropsLaboratory: [],
      headquarter: null,
      headquarters: [],
      showReactionsModal: false,
      initialData: null,
    };
  },
  computed: {
    ...mapState("session", ["permissions"]),
    ...mapState("laboratory", ["laboratories"]),
    selectedAllCrops() {
      return this.cropsLaboratory.length === this.crops.length;
    },
    hasChanges() {
      if (!this.initialData) return false;

      return (
        this.reactions !== this.initialData.reactions ||
        JSON.stringify(this.cropsLaboratory.map((c) => c.en_text)) !==
          JSON.stringify(this.initialData.crops) ||
        (this.headquarter?._id || null) !==
          (this.initialData.headquarter || null)
      );
    },
  },
  methods: {
    ...mapMutations("laboratory", ["SET_PACKAGE_SELECTED"]),
    ...mapMutations("alerts", ["SET_ALERT_TYPE"]),
    ...mapActions("laboratory", [
      "getLaboratories",
      "updateLaboratory",
      "getPackages",
    ]),
    ...mapActions("organization", ["getOrganizationsHeadquarters"]),
    hasPermission(permission) {
      return this.permissions?.includes(permission);
    },
    async loadData() {
      try {
        const req = await this.getLaboratories({ searchCMDA: 1 });

        if (!req.error) {
          this.laboratory = this.laboratories.find((lab) =>
            this.laboratory._id
              ? lab._id.toString() === this.laboratory._id.toString()
              : lab._id.toString() === this.laboratories[0]?._id.toString()
          ) || { name: "-", city: "-" };

          this.formatLab();
        }
      } catch (error) {
        this.showError(error.message);
      }
    },
    toggle() {
      this.cropsLaboratory = this.selectedAllCrops ? [] : [...this.crops];
    },
    async updateLabData() {
      try {
        if (this.cropsLaboratory.length === 0) {
          throw new Error("Debes seleccionar al menos un cultivo");
        }
        const labData = {
          _id: this.laboratory._id,
          headquarter: this.headquarter?._id || null,
          cmda_properties: {
            crops: this.cropsLaboratory.map((c) => c.en_text),
            mos: this.formatMos(),
          },
        };

        const req = await this.updateLaboratory(labData);

        if (req.error) throw new Error(req.message);

        this.showSuccess("Laboratorio actualizado correctamente");
        await this.loadData();
        this.initialData = this.getCurrentDataSnapshot();
      } catch (error) {
        this.showError(error.message);
      }
    },

    formatMos() {
      const labLetter = this.laboratory.name.substring(5, 6);
      let mos = [];

      if (this.laboratory.cmda_properties?.mos) {
        const lastReactions = this.laboratory.cmda_properties.reactions;
        mos = [...this.laboratory.cmda_properties.mos];

        if (this.reactions > lastReactions) {
          for (let i = 0; i < this.reactions - lastReactions; i++) {
            mos.push(
              this.createReactionTemplate(lastReactions + i + 1, labLetter)
            );
          }
        } else if (this.reactions < lastReactions) {
          mos = mos.slice(0, this.reactions);
        }
      } else {
        for (let i = 0; i < this.reactions; i++) {
          mos.push(this.createReactionTemplate(i + 1, labLetter));
        }
      }

      return mos;
    },

    createReactionTemplate(index, labLetter) {
      return {
        reaction: `R${index}-${labLetter}`,
        crops: [],
        fam: null,
        famPathogen: false,
        hex: null,
        hexPathogen: false,
        cy5: null,
        cy5Pathogen: false,
      };
    },

    formatLab() {
      if (this.laboratory?.cmda_properties) {
        this.reactions = this.laboratory.cmda_properties.reactions;
        this.cropsLaboratory = this.laboratory.cmda_properties.crops
          .map((crop) => this.crops.find((c) => c.en_text === crop))
          .filter(Boolean);
      } else {
        this.reactions = 1;
        this.cropsLaboratory = [];
      }

      this.headquarter = this.laboratory.headquarter
        ? this.headquarters.find((h) => h._id === this.laboratory.headquarter)
        : null;

      this.initialData = this.getCurrentDataSnapshot();
    },

    getCurrentDataSnapshot() {
      return {
        reactions: this.reactions,
        crops: this.cropsLaboratory.map((c) => c.en_text),
        headquarter: this.headquarter?._id || null,
      };
    },

    async getPackagesLab(packages = null) {
      try {
        if (!packages && this.laboratory?._id) {
          const req = await this.getPackages({
            laboratory: this.laboratory._id,
          });
          if (req.error) throw new Error(req.message);
          this.packages = req.packages;
        } else if (packages) {
          this.packages = packages;
        }
      } catch (error) {
        this.showError(error.message);
      }
    },

    async getHeadquarters() {
      try {
        const req = await this.getOrganizationsHeadquarters();
        if (req.error) throw new Error(req.message);
        this.headquarters = req.headquarters;
      } catch (error) {
        this.showError(error.message);
      }
    },

    handlePackageSelected(item) {
      this.SET_PACKAGE_SELECTED(item);
      this.showReactionsModal = true;
    },

    showError(message) {
      this.SET_ALERT_TYPE({
        visible: true,
        type: "error",
        text: message,
        timeout: 5000,
      });
    },

    showSuccess(message) {
      this.SET_ALERT_TYPE({
        visible: true,
        type: "success",
        text: message,
        timeout: 4000,
      });
    },

    async refreshTable() {
      await this.loadData();
      await this.getPackagesLab();
    },
  },
  async created() {
    this.user = JSON.parse(localStorage.getItem("user"));
    await this.getHeadquarters();
    await this.loadData();
    await this.getPackagesLab();
  },
  watch: {
    async laboratory() {
      this.formatLab();
      await this.getPackagesLab();
    },
  },
};
</script>

<style scoped>
.package-admin-container {
  height: 100%;
  padding: 8px;
}

.sidebar-col {
  padding-right: 8px;
  height: 100%;
}

.content-col {
  padding-left: 8px;
  height: 100%;
}

.sidebar-card,
.content-card {
  height: 100%;
  overflow-y: auto;
}

.lab-info {
  background-color: #f5f5f5;
  padding: 8px;
  border-radius: 4px;
}

.reactions-input {
  background-color: #f5f5f5;
  padding: 8px;
  border-radius: 4px;
}

@media (max-width: 960px) {
  .package-admin-container {
    padding: 4px;
  }

  .sidebar-col {
    padding-right: 0;
    padding-bottom: 8px;
  }

  .content-col {
    padding-left: 0;
  }

  .sidebar-card {
    margin-bottom: 8px;
  }
}

@media (max-width: 600px) {
  .lab-info,
  .reactions-input {
    padding: 6px;
    font-size: 0.8rem;
  }

  .v-card__title {
    padding: 8px 12px;
  }

  .v-card__text {
    padding: 8px;
  }
}
</style>
